import { Layout } from 'antd';
import {
  DashboardOutlined,
  FlagOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShopOutlined,
  TagsOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@starling/core/auth/store';
import { useLogout } from '@/auth/hooks';
import { AppSidebar } from '@/components/AppSidebar';

const { Sider, Content } = Layout;

// Раскладка раздела «Владелец платформы» (M5.4). Сведена к тому же паттерну,
// что кабинет оптики (AppSidebar): бренд → «Платформа» → навигация → карточка
// пользователя. Верхнего правого хедера больше нет — выход внизу сайдбара.
export function PlatformLayout(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const user = useAuthStore((s) => s.user);

  const path = location.pathname;
  let selectedKey = '/platform';
  if (path.startsWith('/platform/optics')) selectedKey = '/platform/optics';
  else if (path.startsWith('/platform/moderation')) selectedKey = '/platform/moderation';
  else if (path.startsWith('/platform/pricing')) selectedKey = '/platform/pricing';
  else if (path.startsWith('/platform/admins')) selectedKey = '/platform/admins';
  else if (path.startsWith('/platform/sms-journal')) selectedKey = '/platform/sms-journal';
  else if (path.startsWith('/platform/settings')) selectedKey = '/platform/settings';

  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  const roleLabel = t('users.role.platform_admin');
  const userPrimary = user?.name ?? roleLabel;
  const userSecondary = user?.name ? roleLabel : undefined;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        breakpoint="md"
        collapsedWidth={0}
        width={224}
        style={{ position: 'sticky', top: 0, height: '100vh', overflow: 'auto' }}
      >
        <AppSidebar
          brandTitle="Starling"
          brandSubtitle={t('platform.brand_subtitle')}
          contextName={t('platform.brand_subtitle')}
          selectedKey={selectedKey}
          mainSection={{
            label: t('platform.nav.section'),
            items: [
              {
                key: '/platform',
                to: '/platform',
                label: t('platform.nav.sms_center'),
                icon: <DashboardOutlined />,
              },
              {
                key: '/platform/optics',
                to: '/platform/optics',
                label: t('platform.nav.optics'),
                icon: <ShopOutlined />,
              },
              {
                key: '/platform/admins',
                to: '/platform/admins',
                label: t('platform.nav.admins'),
                icon: <SafetyCertificateOutlined />,
              },
              {
                key: '/platform/moderation',
                to: '/platform/moderation',
                label: t('platform.nav.moderation'),
                icon: <FlagOutlined />,
              },
              {
                key: '/platform/sms-journal',
                to: '/platform/sms-journal',
                label: t('platform.nav.journal'),
                icon: <UnorderedListOutlined />,
              },
              {
                // «Платные услуги» — в основном меню, не в «Настройках»: цены —
                // бизнес-логика на виду (решение пользователя 2026-09-11).
                key: '/platform/pricing',
                to: '/platform/pricing',
                label: t('platform.nav.pricing'),
                icon: <TagsOutlined />,
              },
            ],
          }}
          // «Настройки» — в хвостовой секции, как у кабинета оптики
          // (секция «Оптика» → «Настройки»): однотипный сайдбар для обеих
          // ролей (замечание приёмки 2026-09-09).
          tailSection={{
            label: t('platform.nav.section_platform'),
            items: [
              {
                key: '/platform/settings',
                to: '/platform/settings',
                label: t('platform.nav.settings'),
                icon: <SettingOutlined />,
              },
            ],
          }}
          userPrimary={userPrimary}
          userSecondary={userSecondary}
          logoutLabel={t('app.logout')}
          onLogout={handleLogout}
        />
      </Sider>
      <Layout>
        <Content style={{ padding: '24px 32px 40px' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
